import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { IAlert } from 'common/interfaces/IAlert'

import { useFetchSeriesRunQuery, useUpdateSeriesMutation } from 'features/Series/api/series.api'
import { toSeriesApiError } from 'features/Series/api/series.transform'
import type {
  SeriesApiError,
  SeriesRun,
  SeriesSaveWarning,
  SeriesUnit,
  SeriesUpdateBody,
} from 'features/Series/api/series.types'

import { SERIES_TEXT } from 'features/Series/series.constants'

import type { GridEditPatch } from '../controls/SeriesGrid'
import type { GridRow, SeriesFooter, SeriesRowNotes } from '../series.types'
import { seriesFailedNotice } from '../create/seriesAnswer'
import { refusedVisitOf, visitErrorRef } from '../seriesErrors'
import {
  buildUpdateBody,
  draftFromRun,
  editFooter,
  editGridRows,
  maxExtend,
  removedPeople,
  setShiftUnit as setDraftShiftUnit,
  shiftOnward,
  type PersonRemoval,
  type SeriesRunDraft,
} from './seriesEdit.model'

const NOTIFICATION_TITLE = 'Series'
const SAVED = 'Series saved.'
const NO_NOTES: SeriesRowNotes = { locked: [], warnings: [], errors: {} }

export type DraftPatch = Partial<
  Pick<
    SeriesRunDraft,
    'name' | 'note' | 'issueIds' | 'applyIssuesToRemaining' | 'extendBy' | 'cancelled'
  >
>

export type SeriesEdit = {
  status: 'idle' | 'loading' | 'error' | 'ready'
  retry: () => void
  run: SeriesRun | null
  draft: SeriesRunDraft | null
  rows: GridRow[]
  footer: SeriesFooter | null
  openedStep: number | null
  maxExtend: number
  canSave: boolean
  isSaving: boolean
  fieldErrors: Record<string, string[]>
  // Known people the save takes off, for the confirm before it.
  removals: PersonRemoval[]
  editDraft: (patch: DraftPatch) => void
  editVisit: (step: number, patch: GridEditPatch) => void
  setShiftUnit: (unit: SeriesUnit) => void
  shift: (dir: 1 | -1) => void
  save: () => Promise<'closed' | 'open'>
}

export type UseSeriesEditArgs = {
  siteId: string
  resetKey: string
  seriesId: number | null
  openedStep: number | null
  // The opened appointment's own doctor, who cannot be taken off its visit.
  openedDoctorId: number | null
  active: boolean
}

// The run on screen and what is being edited, built once per resetKey.
type Built = { key: string; run: SeriesRun; draft: SeriesRunDraft }

// visits.N is step N; visits.N.field is the Nth row sent; a bare refusal names its step in the text.
export const visitErrorsOf = (
  data: SeriesUpdateBody,
  error: SeriesApiError,
): Record<number, string[]> => {
  const keyed = Object.entries(error.errors).reduce<Record<number, string[]>>(
    (acc, [key, messages]) => {
      const ref = visitErrorRef(key)
      const step = ref?.kind === 'index' ? data.visits?.[ref.n]?.step : ref?.n
      return step === undefined ? acc : { ...acc, [step]: [...(acc[step] ?? []), ...messages] }
    },
    {},
  )
  if (Object.keys(keyed).length || error.code !== 'visit_refused') return keyed
  const refused = refusedVisitOf(error.message)
  return refused ? { [refused.n]: [refused.text] } : keyed
}

const warningText = (warnings: SeriesSaveWarning[]): string =>
  warnings
    .map(({ step, message }) => (step === null ? message : `Visit ${step}: ${message}`))
    .join(' ')

// Cache timestamps are plain instants; they never mean a clinic clock.
const nowMs = (): number => Date.now()

export const useSeriesEdit = ({
  siteId,
  resetKey,
  seriesId,
  openedStep,
  openedDoctorId,
  active,
}: UseSeriesEditArgs): SeriesEdit => {
  const canFetch = active && seriesId !== null
  const query = useFetchSeriesRunQuery(
    { siteId, id: seriesId ?? 0 },
    { skip: !canFetch, refetchOnMountOrArgChange: true },
  )
  const [updateSeries] = useUpdateSeriesMutation()
  const { setNotification } = useNotification()
  const isSendingRef = useRef(false)
  const startedKeyRef = useRef<string | null>(null)
  const currentKeyRef = useRef(resetKey)
  const [isSaving, setIsSaving] = useState(false)
  const [built, setBuilt] = useState<Built | null>(null)
  const [armed, setArmed] = useState(() => ({ key: resetKey, at: nowMs() }))
  const [notes, setNotes] = useState<SeriesRowNotes>(NO_NOTES)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})

  // A new key starts over: nothing built, nothing noted, and the clock restarts.
  if (armed.key !== resetKey) {
    setArmed({ key: resetKey, at: nowMs() })
    setBuilt(null)
    setNotes(NO_NOTES)
    setFieldErrors({})
  }
  const current = armed.key === resetKey ? built : null

  // A run counts only when its fetch finished after this key began.
  const fresh = canFetch && !query.isFetching && (query.fulfilledTimeStamp ?? 0) >= armed.at
  if (fresh && query.data && !current && armed.key === resetKey) {
    setBuilt({ key: resetKey, run: query.data, draft: draftFromRun(query.data) })
  }

  const { refetch } = query
  useEffect(() => {
    currentKeyRef.current = resetKey
  }, [resetKey])
  // The mount fetch covers the first key; a later key on a live query needs its own.
  useEffect(() => {
    if (!canFetch) {
      startedKeyRef.current = null
      return
    }
    const previous = startedKeyRef.current
    startedKeyRef.current = resetKey
    if (previous !== null && previous !== resetKey) void refetch()
  }, [canFetch, resetKey, refetch])

  const retry = useCallback(() => {
    setArmed({ key: resetKey, at: nowMs() })
    setBuilt(null)
    void refetch()
  }, [refetch, resetKey])

  const failed = (query.isError || (fresh && query.data === null)) && !query.isFetching
  let status: SeriesEdit['status'] = 'loading'
  if (!canFetch) status = 'idle'
  else if (current) status = 'ready'
  else if (failed) status = 'error'

  const run = current?.run ?? null
  const draft = current?.draft ?? null

  const patchDraft = useCallback(
    (change: (prev: SeriesRunDraft, base: SeriesRun) => SeriesRunDraft) =>
      setBuilt((prev) => (prev ? { ...prev, draft: change(prev.draft, prev.run) } : prev)),
    [],
  )

  const editDraft = useCallback(
    (patch: DraftPatch) => patchDraft((prev) => ({ ...prev, ...patch })),
    [patchDraft],
  )

  const editVisit = useCallback(
    (step: number, patch: GridEditPatch) =>
      patchDraft((prev) => ({
        ...prev,
        visits: { ...prev.visits, [step]: { ...prev.visits[step], ...patch } },
      })),
    [patchDraft],
  )

  const setShiftUnit = useCallback(
    (unit: SeriesUnit) => patchDraft((prev) => setDraftShiftUnit(prev, unit)),
    [patchDraft],
  )

  const shift = useCallback(
    (dir: 1 | -1) => {
      if (openedStep === null) return
      patchDraft((prev, base) => shiftOnward(base, prev, openedStep, dir))
    },
    [patchDraft, openedStep],
  )

  const failureNotice = (error: SeriesApiError): IAlert =>
    error.code === 'emr_unavailable'
      ? {
          title: NOTIFICATION_TITLE,
          type: ALERT_CONSTANTS.ERROR,
          description: SERIES_TEXT.emrUnavailableEdit,
        }
      : seriesFailedNotice(error)

  const reportFailure = (data: SeriesUpdateBody, error: SeriesApiError) => {
    setNotification(failureNotice(error))
    if (error.code === 'emr_unavailable') {
      retry()
      return
    }
    setFieldErrors(error.errors)
    setNotes({ ...NO_NOTES, errors: visitErrorsOf(data, error) })
  }

  const send = async (base: Built, data: SeriesUpdateBody): Promise<'closed' | 'open'> => {
    isSendingRef.current = true
    setIsSaving(true)
    try {
      const result = await updateSeries({ siteId, id: base.run.id, data }).unwrap()
      const sameKey = () => currentKeyRef.current === base.key
      setNotification(
        result.warnings.length
          ? {
              title: NOTIFICATION_TITLE,
              type: ALERT_CONSTANTS.WARNING,
              description: warningText(result.warnings),
            }
          : { title: NOTIFICATION_TITLE, type: ALERT_CONSTANTS.SUCCESS, description: SAVED },
      )
      if (!sameKey()) return 'closed'
      setFieldErrors({})
      setNotes({ ...NO_NOTES, locked: result.locked, warnings: result.warnings })
      if (result.series) {
        const next = result.series
        setBuilt((prev) =>
          prev && prev.key === base.key
            ? { key: prev.key, run: next, draft: draftFromRun(next) }
            : prev,
        )
      } else {
        setArmed({ key: resetKey, at: nowMs() })
        setBuilt(null)
        void refetch()
      }
      return result.locked.length ? 'open' : 'closed'
    } catch (error) {
      const apiError = toSeriesApiError(error)
      if (currentKeyRef.current === base.key) reportFailure(data, apiError)
      else setNotification(failureNotice(apiError))
      return 'open'
    } finally {
      isSendingRef.current = false
      setIsSaving(false)
    }
  }

  const body = current ? buildUpdateBody(current.run, current.draft) : null
  // A blank name is never sent, so it blocks the other changes too.
  const nameOk = (current?.draft.name.trim() ?? '') !== ''

  const save = async (): Promise<'closed' | 'open'> => {
    if (isSendingRef.current || !current || !body || !nameOk) return 'open'
    return send(current, body)
  }

  const rows = useMemo(
    () => (run && draft ? editGridRows(run, draft, openedStep ?? 0, notes, openedDoctorId) : []),
    [run, draft, openedStep, notes, openedDoctorId],
  )
  const removals = useMemo(() => (run && draft ? removedPeople(run, draft) : []), [run, draft])
  const footer = useMemo(
    () => (run && draft ? editFooter(run, draft, openedStep ?? 0, notes) : null),
    [run, draft, openedStep, notes],
  )

  return {
    status,
    retry,
    run,
    draft,
    rows,
    footer,
    openedStep,
    maxExtend: run ? maxExtend(run) : 0,
    canSave: body !== null && nameOk && !isSaving,
    isSaving,
    fieldErrors,
    removals,
    editDraft,
    editVisit,
    setShiftUnit,
    shift,
    save,
  }
}
